/**
 * pages/Blacklist.jsx — Network Blacklist Management (V2)
 *
 * City-wide blacklist shared across the Founding 30 network.
 * Blacklisted by any agency = blocked everywhere.
 * Robot 3 (Risk Watchman) enforces the block on every rental INSERT.
 * Uses ui.jsx components + CSS variables + i18n t() translations.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { blacklist as blApi } from '../services/api';
import { formatDate } from '../utils/formatters';
import {
  PageHeader, Badge, Card, SearchInput, EmptyState,
  SkeletonTable, StatCard, GlassModal,
} from '../components/ui';

// ── Helpers ──────────────────────────────────────────────────────────
function maskPhone(phone) {
  if (!phone || phone.length < 4) return '—';
  return '•••• ' + phone.slice(-4);
}

// ── Add to Blacklist Modal ──────────────────────────────────────────
function AddBlacklistModal({ open, onClose, onSubmit }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    full_name: '', cin_number: '', passport_number: '', phone: '', reason: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ full_name: '', cin_number: '', passport_number: '', phone: '', reason: '' });
      setError(null);
    }
  }, [open]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async () => {
    setError(null);
    if (!form.full_name.trim()) { setError(t('blacklist.fullNameRequired')); return; }
    if (!form.cin_number && !form.passport_number) { setError(t('blacklist.cinPassportRequired')); return; }
    if (!form.reason || form.reason.length < 10) { setError(t('blacklist.reasonRequired')); return; }
    setSubmitting(true);
    try {
      await onSubmit({
        full_name: form.full_name,
        cin_number: form.cin_number || null,
        passport_number: form.passport_number || null,
        phone: form.phone || null,
        reason: form.reason,
      });
      onClose();
    } catch (e) { setError(e.message || t('blacklist.addError')); }
    setSubmitting(false);
  };

  const inputStyle = {
    width: '100%', height: 44, padding: '0 12px', background: 'var(--elevated)',
    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    color: 'var(--text)', fontSize: 'var(--text-body)', fontFamily: 'inherit', outline: 'none',
  };
  const labelStyle = {
    fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-secondary)',
    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, display: 'block',
  };

  return (
    <GlassModal open={open} onClose={onClose} title={t('blacklist.addTitle')} subtitle={t('blacklist.addSubtitle')} width={520}>
      <div style={{ padding: 'var(--space-xl)', display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
        {error && (
          <div style={{ padding: 'var(--space-md)', background: 'var(--status-alert-bg)', border: '1px solid var(--red)', borderRadius: 'var(--radius-sm)', color: 'var(--red)', fontSize: 'var(--text-body)' }}>{error}</div>
        )}
        <div>
          <label style={labelStyle}>{t('blacklist.fullName')}</label>
          <input value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder={t('blacklist.namePlaceholder')} style={inputStyle} />
        </div>
        <div className="modal-form-row">
          <div>
            <label style={labelStyle}>{t('blacklist.cin')}</label>
            <input value={form.cin_number} onChange={e => set('cin_number', e.target.value)} placeholder="AB123456" style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
          </div>
          <div>
            <label style={labelStyle}>{t('blacklist.passport')}</label>
            <input value={form.passport_number} onChange={e => set('passport_number', e.target.value)} placeholder="P1234567" style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
          </div>
        </div>
        {!form.cin_number && !form.passport_number && (
          <div style={{ fontSize: 'var(--text-caption)', color: 'var(--orange)', marginTop: -4 }}>{t('blacklist.cinOrPassportHint')}</div>
        )}
        <div>
          <label style={labelStyle}>{t('blacklist.phone')}</label>
          <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+212 6XX XXX XXX" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>{t('blacklist.blockReason')}</label>
          <textarea value={form.reason} onChange={e => set('reason', e.target.value)} placeholder={t('blacklist.describePlaceholder')} rows={3} style={{ ...inputStyle, height: 'auto', padding: '10px 12px', resize: 'vertical' }} />
          {form.reason && form.reason.length < 10 && (
            <div style={{ fontSize: 'var(--text-caption)', color: 'var(--orange)', marginTop: 4 }}>{t('blacklist.charsRemaining', { count: 10 - form.reason.length })}</div>
          )}
        </div>
      </div>
      <div style={{ padding: 'var(--space-lg) var(--space-xl)', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-md)' }}>
        <button onClick={onClose} style={{ padding: '10px 20px', height: 44, background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 'var(--text-body)', cursor: 'pointer', fontFamily: 'inherit' }}>{t('common.cancel')}</button>
        <button onClick={handleSubmit} disabled={submitting} style={{ padding: '10px 24px', height: 44, background: submitting ? 'var(--text-muted)' : 'var(--red)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-body)', fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
          {submitting ? t('blacklist.adding') : t('blacklist.confirmBlock')}
        </button>
      </div>
    </GlassModal>
  );
}

// ── Lift Confirmation Modal ─────────────────────────────────────────
function LiftModal({ open, entry, onClose, onConfirm }) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [liftError, setLiftError] = useState(null);

  const handleConfirm = async () => {
    setConfirming(true);
    setLiftError(null);
    try { await onConfirm(entry.id); onClose(); } catch (e) { setLiftError(e.message || t('common.error')); }
    setConfirming(false);
  };

  return (
    <GlassModal open={open} onClose={onClose} title={t('blacklist.liftTitle')} subtitle={t('blacklist.liftSubtitle')} width={440}>
      <div style={{ padding: 'var(--space-xl)' }}>
        <div style={{ background: 'var(--status-alert-bg)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-lg)', marginBottom: 'var(--space-lg)', fontSize: 'var(--text-body)', color: 'var(--text)', lineHeight: 1.6 }}>
          {entry && <>
            <div><strong>{t('blacklist.nameLabel')} :</strong> {entry.full_name}</div>
            {entry.cin_last4 && <div><strong>{t('blacklist.cin')} :</strong> ••••{entry.cin_last4}</div>}
            <div><strong>{t('blacklist.reason')} :</strong> {entry.reason}</div>
            {entry.flagged_by_agency && <div><strong>{t('blacklist.reportedBy')} :</strong> {entry.flagged_by_agency}</div>}
          </>}
        </div>
        <p style={{ fontSize: 'var(--text-body)', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 0 }}>
          {t('blacklist.liftWarning')}
        </p>
      </div>
      {liftError && (
        <div style={{ margin: '0 var(--space-xl) var(--space-sm)', padding: '10px 14px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: '#C53030', lineHeight: 1.4 }}>
          {liftError}
        </div>
      )}
      <div style={{ padding: 'var(--space-lg) var(--space-xl)', borderTop: '1px solid var(--border)', display: 'flex', gap: 'var(--space-md)' }}>
        <button onClick={onClose} style={{ flex: 1, height: 44, background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 'var(--text-body)', cursor: 'pointer', fontFamily: 'inherit' }}>{t('common.cancel')}</button>
        <button onClick={handleConfirm} disabled={confirming} style={{ flex: 1, height: 44, background: confirming ? 'var(--text-muted)' : 'var(--green)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-body)', fontWeight: 700, cursor: confirming ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
          {confirming ? t('common.processing') : t('blacklist.confirmLift')}
        </button>
      </div>
    </GlassModal>
  );
}

// ── Zero-value stats fallback (API down = show nothing, not fake data) ──
const EMPTY_STATS = { total: 0, active_count: 0, lifted_count: 0 };

// ── Main Component ───────────────────────────────────────────────────
export default function Blacklist() {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [liftEntry, setLiftEntry] = useState(null);
  const abortRef = useRef(null);

  const fetchData = useCallback(async (query) => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const results = await Promise.allSettled([
        blApi.list(query || undefined),
        blApi.stats(),
      ]);

      if (ctrl.signal.aborted) return;

      const [listRes, statsRes] = results;

      if (listRes.status === 'fulfilled' && listRes.value) setEntries(listRes.value.entries || []);
      if (statsRes.status === 'fulfilled' && statsRes.value) setStats(statsRes.value);

      if (listRes.status === 'rejected') {
        setEntries([]);
        setStats(null);
        setError(listRes.reason?.message || t('system.serviceUnavailable'));
      } else {
        setError(null);
      }
    } catch (e) {
      if (!ctrl.signal.aborted) { setError(e.message); setEntries([]); setStats(null); }
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(search);
    return () => abortRef.current?.abort();
  }, [fetchData, search]);

  const handleAdd = async (data) => {
    await blApi.add(data);
    fetchData(search);
  };

  const handleLift = async (identityId) => {
    await blApi.remove(identityId);
    fetchData(search);
  };

  const fs = stats || EMPTY_STATS;

  const addButton = (
    <button onClick={() => setShowAddModal(true)} style={{
      padding: '10px 20px', height: 44, background: 'var(--red)', color: '#fff', border: 'none',
      borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-body)', fontWeight: 700, cursor: 'pointer',
      fontFamily: 'inherit', letterSpacing: '0.02em', whiteSpace: 'nowrap',
    }}>+ {t('blacklist.report')}</button>
  );

  return (
    <div style={{ animation: 'fadeInUp 400ms var(--ease-spring) both' }}>
      <PageHeader title={t('blacklist.title')} subtitle={t('blacklist.blockedCount', { count: fs.active_count || entries.length })} action={addButton} />

      {/* Stat cards */}
      <div className="grid-stats" style={{ marginBottom: 'var(--space-xl)' }}>
        <StatCard label={t('blacklist.totalReports')} value={fs.total ?? entries.length} index={0} />
        <StatCard label={t('blacklist.activeBlocked')} value={fs.active_count ?? entries.filter(e => e.is_blacklisted).length} trendUp={false} index={1} />
        <StatCard label={t('blacklist.lifted')} value={fs.lifted_count ?? 0} trendUp={true} index={2} />
      </div>

      {/* Search */}
      <div style={{ marginBottom: 'var(--space-xl)' }}>
        <SearchInput value={search} onChange={setSearch} placeholder={t('blacklist.searchPlaceholder')} style={{ maxWidth: 420 }} />
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: 'var(--space-lg)', background: 'var(--status-alert-bg)', border: '1px solid var(--red)', borderRadius: 'var(--radius)', color: 'var(--red)', fontSize: 'var(--text-body)', marginBottom: 'var(--space-xl)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{t('common.errorPrefix')} {error}</span>
          <button onClick={() => { setError(null); setLoading(true); fetchData(search); }} style={{ padding: '6px 16px', background: 'transparent', border: '1px solid var(--red)', borderRadius: 'var(--radius-sm)', color: 'var(--red)', fontSize: 'var(--text-caption)', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>{t('common.retry')}</button>
        </div>
      )}

      {/* Loading */}
      {loading && <SkeletonTable rows={5} />}

      {/* Empty */}
      {!loading && entries.length === 0 && !search && (
        <Card><EmptyState icon={null} title={t('blacklist.empty')} description={t('blacklist.emptyDesc')} /></Card>
      )}

      {/* Empty search */}
      {!loading && entries.length === 0 && search && (
        <Card><EmptyState icon={null} title={t('common.noResults')} description={t('blacklist.noSearchResults', { query: search })} ctaLabel={t('blacklist.clear')} onCta={() => setSearch('')} /></Card>
      )}

      {/* Table */}
      {!loading && entries.length > 0 && (
        <div className="responsive-table" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {[t('blacklist.identity'), t('blacklist.phone'), t('blacklist.reason'), t('blacklist.date'), t('blacklist.agencies'), t('common.status'), t('common.actions')].map(h => (
                  <th key={h} style={{ padding: '12px 16px', fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'left', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, i) => (
                <tr key={entry.id || i} style={{
                  borderLeft: entry.is_blacklisted ? '3px solid var(--red)' : '3px solid transparent',
                  transition: 'background var(--duration-fast) var(--ease-smooth)',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--raised)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td data-label={t('blacklist.identity')} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 'var(--text-body)', fontWeight: 600, color: 'var(--text)' }}>{entry.full_name}</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                      {entry.cin_last4 ? `CIN ••••${entry.cin_last4}` : '—'}
                    </div>
                  </td>
                  <td data-label={t('blacklist.phone')} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 'var(--text-body)', color: 'var(--text-secondary)' }}>
                    {maskPhone(entry.phone)}
                  </td>
                  <td data-label={t('blacklist.reason')} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 'var(--text-body)', color: 'var(--text)', maxWidth: 220 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }} title={entry.reason}>
                      {entry.reason || '—'}
                    </div>
                  </td>
                  <td data-label={t('blacklist.date')} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 'var(--text-body)', color: 'var(--text-secondary)' }}>
                    {formatDate(entry.blacklisted_at)}
                  </td>
                  <td data-label={t('blacklist.agencies')} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 'var(--text-body)', color: 'var(--status-rented)' }}>
                    {entry.flagged_by_agency || '—'}
                  </td>
                  <td data-label={t('common.status')} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                    <Badge label={entry.is_blacklisted ? t('blacklist.blocked') : t('blacklist.liftedStatus')} variant={entry.is_blacklisted ? 'danger' : 'neutral'} />
                  </td>
                  <td data-label={t('common.actions')} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                    {entry.is_blacklisted && (
                      <button onClick={() => setLiftEntry(entry)} style={{
                        padding: '4px 10px', background: 'var(--status-available-bg)', color: 'var(--status-available)',
                        border: '1px solid rgba(74,222,128,0.3)', borderRadius: 6, fontSize: 'var(--text-label)',
                        cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
                      }}>{t('blacklist.lift')}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Network info banner */}
      {!loading && (
        <div style={{
          marginTop: 'var(--space-xl)', padding: 'var(--space-lg)',
          background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
          fontSize: 'var(--text-body)', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 'var(--space-md)',
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          <div>
            <strong style={{ color: 'var(--text-secondary)' }}>{t('blacklist.robotName')}</strong>
            <span style={{ marginLeft: 'var(--space-sm)' }}>
              {t('blacklist.robotDesc')}
            </span>
          </div>
        </div>
      )}

      {/* Modals */}
      <AddBlacklistModal open={showAddModal} onClose={() => setShowAddModal(false)} onSubmit={handleAdd} />
      <LiftModal open={!!liftEntry} entry={liftEntry} onClose={() => setLiftEntry(null)} onConfirm={handleLift} />
    </div>
  );
}
